/**
 * The `/api/v1/qc` shapes, mirrored from the api worker.
 *
 * Quality control is a Pro+ feature: the api gates the whole `/qc` prefix on the tenant's tier and
 * answers `feature_required` with an upgrade URL below that. Inside a paying tenant the qc.* keys
 * decide who may act — two separate questions, deliberately not merged.
 */

export interface ApiEnvelope {
  ok?: boolean;
  error?: string;
}

/** The four answers a check item can carry. `na` is load-bearing: the paper says the checklist is
 *  intentionally broad and not every item applies to every job, so without it people mark
 *  irrelevant items as passing and the data stops meaning anything. */
export type QcItemState = 'pass' | 'fail' | 'needs_review' | 'na';

export const QC_ITEM_STATES: readonly QcItemState[] = ['pass', 'fail', 'needs_review', 'na'];

/** Fail and needs-review are what block sign-off until fixed or waived. */
export const QC_OUTSTANDING_STATES: readonly QcItemState[] = ['fail', 'needs_review'];

export type QcItemType = 'check' | 'selection' | 'field';

export type QcChecklistStatus =
  | 'draft'
  | 'in_progress'
  | 'submitted'
  | 'changes_requested'
  | 'approved';

export interface QcTemplateSummary {
  id: number;
  name: string;
  description: string | null;
  versionId: number;
  version: number;
  itemCount: number;
}

export interface QcTemplatesResponse extends ApiEnvelope {
  templates?: QcTemplateSummary[];
}

/** A row on the "what is not finished" screen. */
export interface QcChecklistSummary {
  id: number;
  name: string;
  status: QcChecklistStatus;
  dueDate: string | null;
  jobId: number | null;
  jobName: string | null;
  templateName: string;
  itemCount: number;
  answeredCount: number;
  /** Fail or needs-review, not yet waived. Non-zero is what will block sign-off. */
  outstandingCount: number;
  createdAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  /**
   * Set only on a checklist that has been retired. Non-null rows come back ONLY when the request
   * asked for them with `show_archived=1`, so the row says what it is rather than the screen
   * inferring it from the filter it happened to send.
   */
  archivedAt: string | null;
}

export interface QcChecklistsResponse extends ApiEnvelope {
  checklists?: QcChecklistSummary[];
}

export interface QcItem {
  id: number;
  label: string;
  itemType: QcItemType;
  /** The item cannot be marked Pass without a photo. Fail and needs-review are exempt on purpose. */
  photoRequired: boolean;
  state: QcItemState | null;
  valueText: string | null;
  approvedByText: string | null;
  note: string | null;
  answeredByName: string | null;
  answeredAt: string | null;
  photoCount: number;
  waiver: { reason: string; waivedAt: string } | null;
}

export interface QcSection {
  title: string;
  description: string | null;
  items: QcItem[];
}

export interface QcChecklistDetail extends ApiEnvelope {
  checklist?: {
    id: number;
    name: string;
    status: QcChecklistStatus;
    dueDate: string | null;
    jobId: number | null;
    templateName: string;
    templateVersion: number;
    createdAt: string;
    submittedAt: string | null;
    approvedAt: string | null;
    /** An archived checklist stays readable by id — a link in an email has to keep working. */
    archivedAt: string | null;
  };
  fields?: Array<{ key: string; value: string | null }>;
  assignees?: Array<{ userId: number; name: string; source: string; addedAt: string }>;
  sections?: QcSection[];
  /** What the CALLER may do. Render no control the server would refuse. */
  access?: { canAnswer: boolean; canApprove: boolean };
}

export interface QcCreateChecklistInput {
  templateId: number;
  /** Optional by design — a checklist can hang off a job or stand on its own. */
  jobId?: number | null;
  name?: string;
  dueDate?: string | null;
}

export interface QcCreateChecklistResponse extends ApiEnvelope {
  checklist?: { id: number; name: string; status: QcChecklistStatus };
}

/**
 * One answer. `state` for a check; `valueText` (+ `approvedByText`) for a selection; `valueText`
 * alone for a project-information field.
 */
export interface QcAnswerInput {
  state?: QcItemState | null;
  valueText?: string | null;
  approvedByText?: string | null;
  note?: string | null;
}

export interface QcAnswerResponse extends ApiEnvelope {
  item?: {
    id: number;
    state: QcItemState | null;
    valueText: string | null;
    approvedByText: string | null;
    note: string | null;
    answeredByName: string | null;
    answeredAt: string;
  };
}

/**
 * A workflow response. The refusals are the interesting part, and they carry their COUNT — the
 * screen can say "11 still unanswered" instead of "that didn't work".
 */
export interface QcWorkflowResponse extends ApiEnvelope {
  checklist?: { id: number; status: QcChecklistStatus };
  /** Present on a successful submit or waive: what still blocks sign-off. */
  outstanding?: number;
  /** Present on an `unanswered_items` refusal. */
  unanswered?: number;
}

/**
 * The QR link. The token names a CHECKLIST and carries no identity — the scanner must already be a
 * signed-in employee of the tenant, so a photographed code reaches the form but never the door.
 */
export interface QcShareResponse extends ApiEnvelope {
  token?: string;
  /** Path only; the client prefixes the tenant host it is already on. */
  path?: string;
  /** True when a previously issued link has just stopped working. */
  replacedPreviousLink?: boolean;
}

/**
 * Retiring a checklist. Two operations, and the difference is deliberate.
 *
 * ARCHIVE is reversible, allowed at any status, and changes nothing about what was inspected.
 * DELETE destroys, and the server answers 409 `checklist_approved` once a checklist has been signed
 * off — sign-off is where it stops being a work item and becomes evidence. The client mirrors that
 * boundary by not offering the control, but the server is what enforces it.
 */
export interface QcArchiveResponse extends ApiEnvelope {
  checklist?: { id: number; archived: boolean };
}

export interface QcDeleteResponse extends ApiEnvelope {
  deleted?: { id: number };
}

export interface QcJoinResponse extends ApiEnvelope {
  checklistId?: number;
  /** True only when this scan ADDED the caller to the crew list. A re-scan is a no-op. */
  joined?: boolean;
}
